// Vendors
import { useRef } from 'react';
import { NavigateFunction, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Services
import RecipientService from '@services/RecipientService';
// Components
import { StringOption } from '@components/WilmaAsyncSelect/WilmaAsyncSelect';
// Types
import { IUser } from '@appTypes/user';
import { IRecipientId, IRecord } from '@appTypes/recipients';
import { IDiscussionThreadSelectPayload } from '@services/payloads/servicePayloads';
// Context
import { useToast } from '@context/ToastContext';
import { MessagingState } from '@context/Context';
import { ActionType } from '@context/ActionTypes';
import { ROLE_TYPES } from '@utils/constants';

export const useFetchRecipients = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const ref = useRef<NavigateFunction>();
    ref.current = navigate;
    const { createToast } = useToast();
    const {
        state: { message },
        dispatch,
    } = MessagingState();
    const location = useLocation();

    const redirectToComposePage = () => {
        if (!location.search.includes('search')) {
            ref.current && ref.current('/received/page/1/compose');
        }
    };

    const searchRecordIdAndRoleType = async (recipientIds: IRecipientId[]): Promise<IRecord[]> => {
        // Validate recipientIds
        recipientIds.forEach(id => {
            if (!id.roleType || !id.cardNumber) {
                throw new Error('Recipient id is missing roleType or cardNumber');
            }
        });

        try {
            const response = await RecipientService.postRecipientsInfo(recipientIds);
            return response.data; // Extract the data property
        } catch (error) {
            console.error(error);
            createToast({
                title: t('errors.recipientLoadingFailed'),
                toastType: 'danger',
            });
            return []; // Return an empty array in case of error
        }
    };

    async function fetchRecipients(setMessage: (message: IDiscussionThreadSelectPayload, isInitialLoad: true) => void) {
        const searchParams = new URLSearchParams(location.search);

        // Search for all possible recipient keys
        const recipientIds: IRecipientId[] = ROLE_TYPES.flatMap(roleType =>
            searchParams.getAll(roleType).flatMap(cardNumber => ({
                roleType,
                cardNumber,
            }))
        ).filter(id => typeof id.cardNumber !== 'undefined' && id.cardNumber);

        if (recipientIds.length === 0) {
            setMessage({ ...message }, true);
            redirectToComposePage();
            return;
        }

        const recipientRecords = await searchRecordIdAndRoleType(recipientIds);

        const recordIUsers: IUser[] = recipientRecords.map(record => {
            return { label: record.label, roleGuid: record.roleGuid, roleType: record.roleType };
        });

        const recordStringOptions: StringOption[] = recipientRecords.map(record => {
            return {
                name: record.name,
                label: record.label,
                value: record.roleGuid,
                roleType: record.roleType,
                allowMessaging: record.allowSendingMessage,
                schoolNames: record.schoolNames,
                extraInfo: record.extraInfo,
                isGroup: false,
            };
        });
        setMessage({ ...message, recipients: recordIUsers }, true);
        dispatch({
            type: ActionType.SET_SELECTED_RECIPIENTS,
            payload: { options: recordStringOptions },
        });
        redirectToComposePage();
    }

    return { fetchRecipients };
};
